/**
 * components/commonSearch.tsx
 * Premium search field — ported from SK-Catalog's CommonSearch and reworked to
 * fit SK Hire's pure-grayscale design system (no hue anywhere) with a richer set
 * of micro-interactions:
 *   - focus grows a soft grayscale ring + lifts the field (web boxShadow),
 *   - a left accent bar wipes up on focus,
 *   - the search glyph springs + nudges right on focus,
 *   - the clear ("x") button springs in/out with text presence,
 *   - an optional live result-count pill fades in while typing,
 *   - an optional "↵" enter-hint shows while focused (only when onSubmit is set).
 * Fully controlled: the parent owns `value`/`onChange`. Live-filter friendly —
 * `onSubmit` is optional; the field is useful with pure onChange filtering.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Animated,
} from 'react-native';
import Icon from './icon';
import { FontFamily } from '../theme/typography';
import { LightColors, DarkColors } from '../theme/colors';

type Props = Readonly<{
  placeholder?: string;
  value: string;
  onChange: (text: string) => void;
  onSubmit?: () => void;
  onFocus?: () => void;
  isDark?: boolean;
  autoFocus?: boolean;
  /** When provided (and the query is non-empty) a live match-count pill shows. */
  count?: number | null;
  /** Singular noun for the count pill, e.g. "job" → "3 jobs". */
  countNoun?: string;
}>;

export default function CommonSearch({
  placeholder = 'Search…',
  value,
  onChange,
  onSubmit,
  onFocus,
  isDark = false,
  autoFocus = false,
  count = null,
  countNoun = 'result',
}: Props) {
  const [focused, setFocused] = useState(false);

  /* Native-driver animated values (transform + opacity only). */
  const iconAnim = useRef(new Animated.Value(0)).current;   // 0 blur → 1 focus
  const clearScale = useRef(new Animated.Value(value ? 1 : 0)).current;
  const barAnim = useRef(new Animated.Value(0)).current;    // left accent bar wipe
  const countAnim = useRef(new Animated.Value(0)).current;  // count pill fade/slide

  const hasText = value.length > 0;
  const showCount = hasText && count != null;

  useEffect(() => {
    Animated.spring(clearScale, {
      toValue: hasText ? 1 : 0,
      useNativeDriver: true,
      tension: 320,
      friction: 20,
    }).start();
  }, [hasText, clearScale]);

  useEffect(() => {
    Animated.spring(countAnim, {
      toValue: showCount ? 1 : 0,
      useNativeDriver: true,
      tension: 260,
      friction: 22,
    }).start();
  }, [showCount, countAnim]);

  const runFocusAnims = (to: number) => {
    Animated.parallel([
      Animated.spring(iconAnim, { toValue: to, useNativeDriver: true, tension: 260, friction: 16 }),
      Animated.spring(barAnim, { toValue: to, useNativeDriver: true, tension: 220, friction: 18 }),
    ]).start();
  };

  const handleFocus = () => { setFocused(true); onFocus?.(); runFocusAnims(1); };
  const handleBlur = () => { setFocused(false); runFocusAnims(0); };
  const handleClear = () => onChange('');

  const T = isDark ? dark : light;

  const iconScale = iconAnim.interpolate({ inputRange: [0, 1], outputRange: [1, 1.14] });
  const iconShift = iconAnim.interpolate({ inputRange: [0, 1], outputRange: [0, 1] });
  const barScaleY = barAnim; // 0 → 1 height wipe
  const countTranslate = countAnim.interpolate({ inputRange: [0, 1], outputRange: [6, 0] });

  const wrapperStyle = [
    styles.wrapper,
    { backgroundColor: T.bg, borderColor: focused ? T.activeBorder : T.border },
    Platform.OS === 'web'
      ? ({
          transition: 'border-color 180ms cubic-bezier(.2,.8,.2,1), box-shadow 200ms cubic-bezier(.2,.8,.2,1), transform 200ms cubic-bezier(.2,.8,.2,1)',
          transform: focused ? 'translateY(-1px)' : 'translateY(0px)',
          boxShadow: focused ? T.shadowFocus : T.shadowRest,
        } as any)
      : null,
  ];

  return (
    <View style={wrapperStyle}>
      {/* Left accent bar — wipes up on focus */}
      <Animated.View
        pointerEvents="none"
        style={[
          styles.accentBar,
          { backgroundColor: T.activeBorder, opacity: barAnim, transform: [{ scaleY: barScaleY }] },
        ]}
      />

      {/* Search glyph — springs + nudges right on focus */}
      <Animated.View
        style={[
          styles.iconWrap,
          { transform: [{ scale: iconScale }, { translateX: iconShift }] },
        ]}
      >
        <Icon name="search" size={18} color={focused ? T.activeBorder : T.iconColor} />
      </Animated.View>

      <TextInput
        autoFocus={autoFocus}
        value={value}
        onChangeText={onChange}
        onSubmitEditing={onSubmit}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        placeholderTextColor={T.placeholder}
        returnKeyType="search"
        underlineColorAndroid="transparent"
        selectionColor={T.activeBorder}
        style={[
          styles.input,
          { color: T.text },
          Platform.OS === 'web' && ({ outlineStyle: 'none' } as any),
        ]}
      />

      <View style={styles.actions}>
        {/* Live match-count pill */}
        {showCount && (
          <Animated.View
            style={[
              styles.countPill,
              { backgroundColor: T.pillBg, borderColor: T.pillLine, opacity: countAnim, transform: [{ translateY: countTranslate }] },
            ]}
          >
            <Text style={[styles.countText, { color: T.pillInk }]} numberOfLines={1}>
              {count} {countNoun}{count === 1 ? '' : 's'}
            </Text>
          </Animated.View>
        )}

        {/* Clear button — springs in/out */}
        <Animated.View style={{ transform: [{ scale: clearScale }], opacity: clearScale }}>
          <TouchableOpacity
            onPress={handleClear}
            hitSlop={{ top: 10, bottom: 10, left: 8, right: 8 }}
            activeOpacity={0.7}
            accessibilityLabel="Clear search"
            style={[
              styles.clearBtn,
              { backgroundColor: T.clearBg },
              Platform.OS === 'web' ? ({ transition: 'background 140ms ease' } as any) : null,
            ]}
          >
            <Icon name="x" size={12} color={T.clearIcon} />
          </TouchableOpacity>
        </Animated.View>

        {/* Enter hint (focused, only when a submit handler exists) */}
        {onSubmit && focused && !hasText && (
          <View style={[styles.enterHint, { borderColor: T.pillLine }]}>
            <Text style={[styles.enterHintText, { color: T.iconColor }]}>↵</Text>
          </View>
        )}

        {/* Submit arrow — only when the parent wires up onSubmit */}
        {onSubmit && (
          <>
            {hasText && <View style={[styles.divider, { backgroundColor: T.divider }]} />}
            <TouchableOpacity
              onPress={onSubmit}
              activeOpacity={0.65}
              accessibilityLabel="Search"
              style={[
                styles.submitBtn,
                { backgroundColor: focused ? T.activeBorder : T.submitBg },
                Platform.OS === 'web' ? ({ transition: 'background 160ms ease' } as any) : null,
              ]}
            >
              <Icon name="arrow-r" size={14} color={focused ? T.submitIconActive : T.submitIcon} />
            </TouchableOpacity>
          </>
        )}
      </View>
    </View>
  );
}

/* ─────────────────────────────────────────
   Theme tokens — PURE GRAYSCALE (no hue). Shadows carry meaning via depth only.
───────────────────────────────────────── */
const light = {
  bg: LightColors.card,
  border: LightColors.lineStrong,
  activeBorder: LightColors.accent,
  text: LightColors.ink,
  placeholder: LightColors.ink4,
  iconColor: LightColors.ink4,
  clearBg: LightColors.paper2,
  clearIcon: LightColors.ink3,
  divider: LightColors.line,
  submitBg: LightColors.paper2,
  submitIcon: LightColors.ink4,
  submitIconActive: LightColors.white,
  pillBg: LightColors.paper2,
  pillLine: LightColors.line,
  pillInk: LightColors.ink2,
  shadowRest: '0 1px 2px rgba(0,0,0,0.04)',
  shadowFocus: '0 0 0 4px rgba(15,15,15,0.06), 0 8px 24px -12px rgba(0,0,0,0.22)',
};

const dark = {
  bg: DarkColors.card,
  border: DarkColors.line,
  activeBorder: DarkColors.accent,
  text: DarkColors.ink,
  placeholder: DarkColors.ink4,
  iconColor: DarkColors.ink4,
  clearBg: DarkColors.paper2,
  clearIcon: DarkColors.ink3,
  divider: DarkColors.line,
  submitBg: DarkColors.paper2,
  submitIcon: DarkColors.ink3,
  submitIconActive: DarkColors.black,
  pillBg: DarkColors.paper3,
  pillLine: DarkColors.lineStrong,
  pillInk: DarkColors.ink2,
  shadowRest: '0 1px 2px rgba(0,0,0,0.4)',
  shadowFocus: '0 0 0 4px rgba(237,237,237,0.10), 0 10px 28px -14px rgba(0,0,0,0.7)',
};

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 44,
    borderRadius: 10, // matches theme Radii.lg (cards) — prominent but on-scale
    borderWidth: 1.5,
    paddingLeft: 14,
    paddingRight: 8,
    gap: 8,
    overflow: 'hidden',
  },
  accentBar: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
  },
  iconWrap: {
    width: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  input: {
    flex: 1,
    minWidth: 0,
    fontSize: 15,
    fontWeight: '500',
    fontFamily: FontFamily.sans,
    paddingVertical: 12,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web'
      ? ({ outlineWidth: 0, outlineStyle: 'none', borderWidth: 0 } as any)
      : {}),
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  countPill: {
    height: 22,
    paddingHorizontal: 9,
    borderRadius: 11,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  countText: {
    fontSize: 11,
    fontWeight: '600',
    fontFamily: FontFamily.mono,
  },
  clearBtn: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  enterHint: {
    minWidth: 22,
    height: 22,
    paddingHorizontal: 6,
    borderRadius: 6,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  enterHintText: {
    fontSize: 12,
    fontWeight: '600',
    fontFamily: FontFamily.mono,
  },
  divider: {
    width: 1,
    height: 18,
    borderRadius: 1,
  },
  submitBtn: {
    width: 34,
    height: 34,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
